import { Button, Input, Text } from '@cretesuite/design-system'
import { IonSpinner } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { validateCode } from '../../helpers/cognitoLogin'
import { useSession } from '../../hooks/useSession'
import { useToast } from '../../hooks/useToast'
import { AuthLayout } from './AuthLayout'
import { reloadTo } from '../../helpers/appReload'

const PRIVACY_POLICY_URL = 'https://www.cretesuite.com/legal/privacy-policy'
const TERMS_OF_USE_URL = 'https://www.cretesuite.com/legal/terms-of-use'

function b64ToUtf8(str: string): string | undefined {
  try {
    return decodeURIComponent(escape(window.atob(str)))
  } catch {
    return undefined
  }
}

function utf8ToB64(str: string | number | boolean) {
  return window.btoa(unescape(encodeURIComponent(str)))
}

/** Multi-org operators pick their organization before the welcome screen. */
const postLoginPath = (multiOrg?: boolean) =>
  multiOrg ? '/select-org' : '/welcome'

export const ValidateCodePage = () => {
  const history = useHistory()
  const location = useLocation()
  const { login } = useSession()
  const [presentToast] = useToast()
  const { _ } = useLingui()

  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const query = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search],
  )

  const username = 'u' in query ? b64ToUtf8(query.u) : undefined
  const session = 's' in query ? b64ToUtf8(query.s) : undefined
  const expires = useMemo(
    () => query?.x && DateTime.fromSeconds(Number.parseInt(query.x)),
    [query?.x],
  )

  const goBack = () => {
    history.replace('/login')
  }

  // Redirect if no username
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when username changes
  useEffect(() => {
    if (!username) {
      goBack()
    }
  }, [username])

  // Code expiration timer
  // biome-ignore lint/correctness/useExhaustiveDependencies: timer is keyed to the expiry instant
  useEffect(() => {
    if (!expires) return

    const diffMs = expires.diffNow().as('milliseconds')

    if (diffMs < 0) {
      presentToast({
        color: 'danger',
        message: _(
          msg`Sorry, the sent code is no longer valid. Please enter your phone number again.`,
        ),
        duration: 4000,
      })
      goBack()
      return
    }

    const timer = setTimeout(() => {
      presentToast({
        color: 'danger',
        message: _(
          msg`Sorry, the sent code is no longer valid. Please enter your phone number again.`,
        ),
        duration: 4000,
      })
      goBack()
    }, diffMs)

    return () => clearTimeout(timer)
  }, [expires])

  // biome-ignore lint/correctness/useExhaustiveDependencies: goBack/setters are stable; deps cover the validated inputs
  const submit = useCallback(async () => {
    if (!username || loading) return

    setError(null)
    setLoading(true)

    try {
      const validateResp = await validateCode({
        username,
        code,
        session: session || undefined,
      })

      const idToken = validateResp?.AuthenticationResult?.IdToken
      if (idToken) {
        const response = await login(idToken)

        if (!response.success) {
          throw response.error || new Error('Authentication failed')
        }

        // Full reload into /welcome instead of an in-app navigation. The
        // session flag flips to logged-in asynchronously; navigating in-app
        // races the auth→app router swap and showed a blank screen. A reload
        // boots the app already authenticated (the same path as reopening the
        // app, which always works). Mirrors v1's window.location use on logout.
        reloadTo(postLoginPath(response.multiOrg))
        return
      }

      // Code mismatch — Cognito returned a new session
      if (validateResp.Session) {
        history.replace({
          pathname: location.pathname,
          search: `?${new URLSearchParams({
            ...query,
            s: utf8ToB64(validateResp.Session),
          })}`,
        })

        setCode('')
        setError(
          _(
            msg`The code you provided did not match. Please try again with the most recent code sent to your phone.`,
          ),
        )
      }
    } catch (err) {
      // Keep the toast generic — never surface raw backend/error text to the
      // user. Log the detail for diagnostics instead.
      console.error('Code validation failed:', err)
      presentToast({
        color: 'danger',
        message: `${_(msg`Error`)}. ${_(msg`Please try again`)}`,
        duration: 4000,
      })
      goBack()
    } finally {
      setLoading(false)
    }
  }, [username, loading, code, session, login, history, location, query, _])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submit()
  }

  return (
    <AuthLayout id='login-validate'>
      <form onSubmit={handleSubmit}>
        {error && (
          <Text variant='body' className='auth-error'>
            {error}
          </Text>
        )}

        <div className='auth-field'>
          <Text variant='label' className='auth-label'>
            {_(msg`Verification code`)}
          </Text>
          <Input
            type='number'
            inputMode='numeric'
            placeholder='000000'
            value={code}
            onChange={(e) => setCode(e.target.value)}
            aria-label={_(msg`Verification code`)}
            data-testid='login-code-input'
          />
        </div>

        <Button
          variant='primary'
          size='lg'
          type='submit'
          disabled={loading || code.length < 6}
          className='auth-button'
          data-testid='login-code-submit'
        >
          {loading ? <IonSpinner name='crescent' /> : _(msg`Login`)}
        </Button>

        <Button
          variant='ghost'
          size='lg'
          type='button'
          onClick={goBack}
          className='auth-back-button'
        >
          {_(msg`Back`)}
        </Button>

        <Text variant='body' className='auth-sms-notice'>
          {_(msg`By logging in, you agree to our`)}{' '}
          <a
            href={PRIVACY_POLICY_URL}
            target='_blank'
            rel='noopener noreferrer'
          >
            {_(msg`Privacy Policy`)}
          </a>{' '}
          &{' '}
          <a href={TERMS_OF_USE_URL} target='_blank' rel='noopener noreferrer'>
            {_(msg`Terms of Use`)}
          </a>
        </Text>
      </form>
    </AuthLayout>
  )
}

export default ValidateCodePage
